import { ReactComponent as WarningIcon } from '@assets/icons/toast-icons/warning.svg';
import Button from '@components/V4/Button';
import InfoBar from '@components/V4/InfoBar';
import Select from '@components/V4/Select/Select';
import { useAuthState } from '@contexts/AuthContext';
import { ZoomContext } from '@modules/zoom/contexts/ZoomContext';
import useLocalTracks from '@modules/zoom/hooks/useLocalTracks';
import { trackAudioEncodeDecodeReadiness } from '@modules/zoom/utils/helpers';
import useToast from 'apps/agora/src/hooks/useToast';
import { useContext, useRef, useEffect, useState } from 'react';
import DeviceTroubleshooting from './DeviceTroubleshooting';
import VideoWidget from './VideoWidget';

const VideoMeetingPreview = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [sdkInitError, setSdkInitError] = useState<string>();
  const { avatar } = useAuthState();
  const {
    zoomClient,
    activeCamera,
    activeMicrophone,
    isMicrophoneActive,
    isCameraActive,
    isZoomClientReady,
    setActiveCamera,
    setActiveMicrophone,
    setIsMicrophoneActive,
    setIsCameraActive,
    setIsZoomClientReady,
  } = useContext(ZoomContext);

  const toast = useToast();

  useEffect(() => {
    if (!zoomClient || isZoomClientReady) return;

    let isSubscribed = true;

    trackAudioEncodeDecodeReadiness(zoomClient);

    zoomClient
      .init('en-US', 'Global', {
        patchJsMedia: true,
        stayAwake: true,
        enforceMultipleVideos: true,
        leaveOnPageUnload: true,
        enforceVirtualBackground: true,
      })
      .then(() => {
        if (isSubscribed) {
          setIsZoomClientReady(true);
        }
      })
      .catch((error) => {
        if (!isSubscribed) return;

        setSdkInitError(error?.message ?? 'Unknown error');
        toast.error(`Zoom SDK initialization failed: ${error?.message}`);
      });

    return () => {
      isSubscribed = false;
    };
  }, [zoomClient, isZoomClientReady]);

  const {
    error: localTracksError,
    initialSetupCompleted,
    camerasWithErrors,
    microphonesWithErrors,
    isMicrophoneSwitchLoading,
    isCameraSwitchLoading,
    isCameraStateSwitchLoading,
    isMicrophoneStateSwitchLoading,
    micList,
    cameraList,
    retryInitializeDevices,
    toggleMicrophone,
    toggleCamera,
    changeMicrophone,
    changeCamera,
  } = useLocalTracks({
    videoRef,
    isCameraActive,
    isMicrophoneActive,
    activeCamera,
    activeMicrophone,
    onActiveMicrophoneChange: setActiveMicrophone,
    onActiveCameraChange: setActiveCamera,
    onSetIsCameraActive: setIsCameraActive,
    onSetIsMicrophoneActive: setIsMicrophoneActive,
  });

  const microphonesWithErrorCount = Object.keys(microphonesWithErrors).length;
  const camerasWithErrorCount = Object.keys(camerasWithErrors).length;

  const didAllMicrophonesFail =
    !!microphonesWithErrorCount && microphonesWithErrorCount === micList.length;
  const didAllCamerasFail = !!camerasWithErrorCount && camerasWithErrorCount === cameraList.length;

  const noCameraDetected = initialSetupCompleted && !cameraList.length && !localTracksError;
  const noMicrophoneDetected = initialSetupCompleted && !micList.length && !localTracksError;

  const showCameraTroubleshooting = didAllCamerasFail || noCameraDetected;
  const showMicrophoneTroubleshooting = didAllMicrophonesFail || noMicrophoneDetected;

  const shouldShowRetryButton = showCameraTroubleshooting || showMicrophoneTroubleshooting;

  return (
    <div className="flex flex-col gap-5 laptop:order-2 laptop:min-w-1/2 laptop:w-1/2 laptop:max-w-[calc(50%-clamp(4rem,7.2vw,6.5rem)/2)]">
      <VideoWidget
        onCameraClick={() => toggleCamera(isCameraActive)}
        onMicrophoneClick={() => toggleMicrophone(isMicrophoneActive)}
        ref={videoRef}
        isCameraButtonLoading={isCameraStateSwitchLoading}
        isMicrophoneButtonLoading={isMicrophoneStateSwitchLoading}
        isCameraActive={isCameraActive}
        isMicrophoneActive={isMicrophoneActive}
        isCameraDisabled={!cameraList.length}
        isMicrophoneDisabled={!micList.length}
        avatarUrl={avatar}
      />
      <div className="hidden flex-row gap-5 justify-between laptop:flex laptop:w-full">
        <Select
          isValid
          isTouched
          options={micList.map((item) => ({
            label: item.label,
            value: item.deviceId,
            icon: microphonesWithErrors[item.deviceId] && (
              <WarningIcon className="min-w-3 h-3 w-3 ml-2" />
            ),
          }))}
          value={activeMicrophone}
          onSelect={(value) => changeMicrophone(value as string)}
          label="Audio Device"
          className="w-1/2 min-w-[calc(50%-0.625rem)] max-w-[calc(50%-0.625rem)]"
          allowClear={false}
          isLoading={isMicrophoneSwitchLoading}
          isDisabled={
            !micList.length || isMicrophoneSwitchLoading || isMicrophoneStateSwitchLoading
          }
        />
        <Select
          isValid
          isTouched
          options={cameraList.map((item) => ({
            label: item.label,
            value: item.deviceId,
            icon: camerasWithErrors[item.deviceId] && (
              <WarningIcon className="min-w-3 h-3 w-3 ml-2" />
            ),
          }))}
          value={activeCamera}
          onSelect={(value) => changeCamera(value as string)}
          label="Video Device"
          className="w-1/2 min-w-[calc(50%-0.625rem)] max-w-[calc(50%-0.625rem)]"
          allowClear={false}
          isLoading={isCameraSwitchLoading}
          isDisabled={!cameraList.length || isCameraSwitchLoading || isCameraStateSwitchLoading}
        />
      </div>
      <InfoBar
        variant="info"
        messageBody="Your virtual background and noise suppression settings can be customized in the meeting settings modal after you join. Any changes you make there will be saved and applied to future meetings as well!"
      />
      {shouldShowRetryButton && (
        <Button
          buttonText="Retry"
          size="large"
          isLoading={isCameraStateSwitchLoading || isMicrophoneStateSwitchLoading}
          onClick={retryInitializeDevices}
        />
      )}
      {sdkInitError && (
        <InfoBar
          variant="error"
          messageTitle="The video meeting library failed to load"
          messageBody={`${sdkInitError}. Please check your connection and reload the page.`}
        />
      )}
      {localTracksError && (
        <>
          <InfoBar
            variant="error"
            messageTitle={localTracksError.message}
            messageBody="Please enable Camera and Microphone permissions and reload the page."
          />
          <DeviceTroubleshooting device="camera" hasRetry={false} />
        </>
      )}
      {showCameraTroubleshooting && <DeviceTroubleshooting device="camera" />}
      {showMicrophoneTroubleshooting && <DeviceTroubleshooting device="microphone" />}
      {!didAllCamerasFail &&
        Object.keys(camerasWithErrors).map((cameraId) => (
          <InfoBar
            key={`camera-error-${cameraId}`}
            variant="error"
            messageTitle={camerasWithErrors[cameraId].message}
            messageBody={`Couldn't start camera ${
              camerasWithErrors[cameraId]?.deviceLabel || ''
            }, please select another Video Device.`}
          />
        ))}
      {!didAllMicrophonesFail &&
        Object.keys(microphonesWithErrors).map((microphoneId) => (
          <InfoBar
            key={`microphone-error-${microphoneId}`}
            variant="error"
            messageTitle={microphonesWithErrors[microphoneId].message}
            messageBody={`Couldn't start microphone ${
              microphonesWithErrors[microphoneId]?.deviceLabel || ''
            }, please select another Input Audio Device.`}
          />
        ))}
    </div>
  );
};

export default VideoMeetingPreview;
